import { Component, computed, inject, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RestService } from 'src/app/services/rest.service';
import { TranslationService } from 'src/app/services/translation.service';
import { Snackbar } from 'src/app/services';
import { SocialLoginConfig } from 'src/app/interfaces/myCustomRoute.interface';

export type SocialLoginProvider = 'google' | 'meta' | 'apple';

/**
 * Botones "Continuar con ..." del login/registro social (HRS-2514).
 *
 * Qué proveedores se muestran lo decide la configuración de la app en el CMS
 * (wizard paso 1 → step1.socialLogin, expuesto en get-common-app). Si ninguno
 * está activo, el componente no pinta nada (tampoco el divisor).
 *
 * Flujo redirect centralizado en el back (docs/plan-social-login.md §0-bis del
 * repo cicerone-back): cada botón navega a /api/auth/{provider}/redirect con el
 * origin y la ruta actual; a la vuelta, AuthService.handleSocialLoginReturn()
 * canjea el código de un solo uso por el token Sanctum.
 */
@Component({
  selector: 'app-social-login-buttons',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './social-login-buttons.component.html',
  styleUrls: ['./social-login-buttons.component.scss'],
})
export class SocialLoginButtonsComponent {
  private _restService = inject(RestService);
  private _snackbarService = inject(Snackbar);
  private _translationService = inject(TranslationService);

  /** Pinta el divisor "o" encima de los botones (modales de registro). */
  @Input() withDivider = false;

  /**
   * Intención del flujo: 'login' solo inicia sesión de cuentas ya registradas
   * (si no existe, el back devuelve social_login_error=not_registered);
   * 'register' además da de alta. Los modales de registro deben pasar 'register'.
   */
  @Input() intent: 'login' | 'register' = 'login';

  /**
   * Aceptación de términos y condiciones (modales de registro): mientras sea
   * false, el clic no redirige al proveedor y se avisa al usuario. Sin binding
   * (undefined, modales de login) no se exige.
   */
  @Input() termsAccepted?: boolean;

  protected providers = computed<SocialLoginProvider[]>(() => {
    const config: SocialLoginConfig | undefined = this._restService.device()?.socialLogin;
    if (!config) return [];
    return (['google', 'meta', 'apple'] as SocialLoginProvider[]).filter((provider) => config[provider]);
  });

  protected labels: Record<SocialLoginProvider, string> = {
    google: 'Continuar con Google',
    meta: 'Continuar con Facebook',
    apple: 'Continuar con Apple',
  };

  protected loginWith(provider: SocialLoginProvider): void {
    if (this.termsAccepted === false) {
      const message = this._translationService.instant('Debes aceptar los términos y condiciones');
      // document.body: el snackbar debe quedar por encima de los overlays (Mat Dialog / modal Ionic).
      this._snackbarService.showErrorSnackbar(message, undefined, document.body);
      return;
    }

    const params = new URLSearchParams({
      origin: window.location.origin,
      path: window.location.pathname,
      intent: this.intent,
    });
    // name_index: origen del registro (HRS-2825); viaja en el state cifrado y
    // el back lo valida contra BD en el alta social
    if (this._restService.subDomain) params.set('name_index', this._restService.subDomain);
    window.location.href = `${this._restService.apiUrlBase}/api/auth/${provider}/redirect?${params.toString()}`;
  }
}
